import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, of, switchMap, take, throwError, timeout } from 'rxjs';
import { ConnectionGateService } from '../connection-gate.service';
import { TokenRefreshService } from '../token-refresh.service';

// The refresh flow's own calls must never trigger (or wait on) another refresh or the gate.
const REFRESH_ROUTES = ['User/RefreshToken', 'ConnectionToken/GenerateConnectionToken'];
// Safety net: if the socket never gets going, calls are released after this long.
const GATE_TIMEOUT_MS = 8000;

/**
 * 1. Gate: every call that carries a Bearer token waits until the websocket is connected and the
 *    token has been replaced (ConnectionGateService), then goes out with the current token.
 * 2. 401: the refresh flow runs once (TokenRefreshService) and the call is sent again with the new
 *    token; calls arriving meanwhile wait for it. If the refresh itself fails, the original 401 is
 *    passed on so existing error handling still runs.
 */
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  if (
    typeof localStorage === 'undefined' ||
    !req.headers.has('Authorization') ||
    REFRESH_ROUTES.some((route) => req.url.includes(route))
  ) {
    return next(req);
  }

  const gate = inject(ConnectionGateService);
  const refresher = inject(TokenRefreshService);

  const withCurrentToken = (r: HttpRequest<unknown>, token = localStorage.getItem('token')) =>
    token ? r.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : r;

  const send = (r: HttpRequest<unknown>) => {
    const sentAuth = r.headers.get('Authorization');
    return next(r).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) return throwError(() => error);
        // Sent with a token that has since been replaced (a refresh finished while this call was in
        // flight): don't refresh again, just send it again with the current token.
        const current = localStorage.getItem('token');
        if (!refresher.isRefreshing && current && `Bearer ${current}` !== sentAuth) {
          return next(withCurrentToken(r, current));
        }
        return refresher.refresh().pipe(
          catchError(() => throwError(() => error)),
          switchMap((token) => next(withCurrentToken(r, token))),
        );
      }),
    );
  };

  const refreshing = () =>
    refresher.isRefreshing ? refresher.refresh().pipe(catchError(() => of(null))) : of(null);

  if (gate.isReady && !refresher.isRefreshing) return send(req);

  return gate.ready$.pipe(
    filter((ready) => ready),
    take(1),
    timeout({ first: GATE_TIMEOUT_MS, with: () => of(true) }),
    switchMap(() => refreshing()),
    switchMap(() => send(withCurrentToken(req))),
  );
};
